<template>
  <ConfirmDialog group="success_connect">
    <template #container="{ message, rejectCallback }: { message: ConfirmationOptions, rejectCallback: () => void }">
      <div class="absolute right-0 top-0 z-10 mr-[8px] mt-[8px] cursor-pointer">
        <Icon
          class="text-[--color-neutral-neutral-lighter]"
          name="times"
          :width="14"
          :height="14"
          @click="() => { rejectCallback(); handleClose(); }"
        />
      </div>
      <BaseDialogFrame
        :header="message.header"
        header-class="v3-regular-medium-line-height-150 text-[--text-primary]"
      >
        <template #body>
          <p class="v3-small-normal-line-height-150 mb-[8px] text-[--v3-global-text-neutral]">
            Your shop has been successfully connected to eRank.
          </p>
          <p class="v3-small-normal-line-height-150 text-[--v3-global-text-neutral]">
            Please allow a few minutes for us to download your data from Etsy.
          </p>
        </template>

        <template #footer>
          <Button
            label="Done"
            @click="() => { rejectCallback(); handleClose(); }"
          />
        </template>
      </BaseDialogFrame>
    </template>
  </ConfirmDialog>
</template>

<script lang="ts" setup>
import { useUrlSearchParams } from '@vueuse/core';
import Button from 'primevue/button';
import type { ConfirmationOptions } from 'primevue/confirmationoptions';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { onMounted } from 'vue';
import { useRoute } from 'vue-router';

import Icon from '@/common/components/atoms/Icon';
import BaseDialogFrame from '@/common/components/molecules/BaseDialogFrame';
import { mixPanelEvents } from '@/common/constants';
import { track } from '@/helpers/analytics/mixpanel';

const props = withDefaults(defineProps<Props>(), {
  redirectTo: 'dashboard',
  shouldRedirect: true,
});
const confirm = useConfirm();
const params = useUrlSearchParams('history');

const route = useRoute();
const page_slug = route.meta?.metaTags?.find((tag) => tag.page_slug)?.page_slug ?? '';
const source = page_slug.replaceAll(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

interface Props {
  redirectTo?: string
  shouldRedirect?: boolean
}

const handleClose = () => {
  localStorage.setItem('etsyShopConnected', 'success');
  if (props.shouldRedirect) {
    window.location.href = `/${props.redirectTo}`;
  } else {
    cleanUrlParams();
  }
};

const cleanUrlParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('success');
  url.searchParams.delete('shop_name');
  window.history.replaceState({}, '', url.toString());
};

onMounted(() => {
  if (params.success && params.shop_name) {
    // Track the completed Etsy connection
    track(mixPanelEvents.CONNECTED_SHOP, {
      'Source': source,
      'Erank Version': 3,
    });

    confirm.require({
      group: 'success_connect',
      header: `${params.shop_name} Connected`,
    });
  }
});
</script>
